import {
  createMuiTheme,
  InputBase,
  MuiThemeProvider,
  Tab,
  Tabs
} from "@material-ui/core";
import { History } from "history";
import { findIndex, propEq, remove } from "ramda";
import * as React from "react";
import { connect } from "react-redux";
import { match } from "react-router-dom";
import Select from "react-select";
import api from "src/api";
import countryOptions from "src/createEntityModal/countries";
import industryOptions from "src/createEntityModal/industries";
import { DropdownOption } from "src/createEntityModal/types";
import Documents from "src/documentList/Documents";
import { formatEntityValue } from "src/documentUpload/helpers";
import { Entity } from "src/documentUpload/types";
import { ENTITY_COLUMNS } from "src/entityList/types";
import { EditIcon } from "src/entityView/EditIcon";
import { isRequesting } from "src/requestProgress/helpers";
import { actions } from "src/requestProgress/reducer";
import { getRequestProgress } from "src/requestProgress/selector";
import { DeleteRuleModal } from "src/ruleView/DeleteRuleModal";
import { RuleCard } from "src/ruleView/RuleCard";
import { showAlertBanner } from "src/shared/alertBanner/actions";
import { ALERT_BANNER_TYPES } from "src/shared/alertBanner/reducer";
import LoadingSpinner from "src/shared/LoadingSpinner";
import QuiqButton from "src/shared/QuiqButton";
import {
  deleteRules,
  DeleteRulesActionType
} from "src/spread/model/spread/actions";
import { RuleChangeLogItem } from "src/spread/model/spread/types";
import selectorsToProps from "src/utils/selectorsToProps";
import colors from "../colors.scss";
import {
  editEntity,
  EditEntityActionType,
  getEntity,
  GetEntityActionType,
  resetEntity
} from "./actions";
import { EntityAuditTrail } from "./EntityAuditTrail";
import "./EntityView.scss";
import { getEntityViewState } from "./selector";
import { EntityChangeModel } from "./types";

const materialTheme = createMuiTheme({
  palette: {
    primary: {
      main: colors.moodysBlue || "#009bdf"
    }
  },
  typography: {
    useNextVariants: true
  }
});

interface EntityViewState {
  isEditing: boolean;
  activeTab: number;
  entityAudits: EntityChangeModel[] | null;
  entityRules: RuleChangeLogItem[] | null;
  isDeleteRuleModalOpen: boolean;
  ruleToDelete: RuleChangeLogItem | null;
  newEntity: Entity | null;
}

interface EntityViewProps {
  history: History;
  match: match<{ id: string }>;
  editEntity: EditEntityActionType;
  getEntity: GetEntityActionType;
  entity: Entity | null;
  resetEntity: typeof resetEntity;
  deleteRulesAction: DeleteRulesActionType;
  showAlertBanner: typeof showAlertBanner;
}

export class EntityView extends React.Component<
  EntityViewProps,
  EntityViewState
> {
  constructor(props: EntityViewProps) {
    super(props);
    this.state = {
      isEditing: false,
      activeTab: 0,
      entityAudits: null,
      entityRules: null,
      isDeleteRuleModalOpen: false,
      ruleToDelete: null,
      newEntity: null
    };
  }

  public handleTabChange = (event: object, newValue: number) => {
    if (this.state.isEditing) {
      this.cancelEdit();
    }
    this.setState({ activeTab: newValue });
  };

  public async componentWillMount() {
    this.props
      .getEntity(this.props.match.params.id)
      .then(entity => this.setState({ newEntity: Object.assign({}, entity) }));
  }

  public componentWillUnmount() {
    this.props.resetEntity();
  }

  public toggleEdit = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (this.state.isEditing) {
      this.saveEdits();
    }
    this.setState({ isEditing: !this.state.isEditing });
  };

  public cancelEdit = () => {
    this.setState({
      isEditing: false,
      newEntity: Object.assign({}, this.props.entity)
    });
  };

  public closeDeleteRuleModal = () => {
    this.setState({
      isDeleteRuleModalOpen: false,
      ruleToDelete: null
    });
  };

  public openDeleteRuleModal = (rule: RuleChangeLogItem) => {
    this.setState({
      isDeleteRuleModalOpen: true,
      ruleToDelete: rule
    });
  };

  public deleteRule = () => {
    if (this.state.ruleToDelete) {
      this.props
        .deleteRulesAction({
          rulesToDelete: [this.state.ruleToDelete]
        })
        .then(this.onDeleteRuleComplete);
    }
  };

  public render() {
    const {
      activeTab,
      newEntity,
      isEditing,
      isDeleteRuleModalOpen
    } = this.state;
    const shouldRenderDetails = activeTab === 0;
    const shouldRenderSpreads = activeTab === 1;
    const shouldRenderRules = activeTab === 2;
    const shouldRenderHistory = activeTab === 3;

    const isSaveBtnDisabled = Boolean(
      isEditing &&
        (!newEntity ||
          (newEntity.name && newEntity.name.trim().length < 1) ||
          (newEntity.city && newEntity.city.trim().length < 1))
    );

    return (
      <div className="Entity__container">
        <div className="Entity__header__container">
          <div className="Entity__header">
            <div className="Entity__header__title">{this.getEntityName()}</div>
            <div className="Entity__header__edit">
              {shouldRenderDetails && isEditing && (
                <QuiqButton
                  className="Entity__edit__cancel"
                  variant="text"
                  onClick={this.cancelEdit}
                  isDisabled={isRequesting(actions.editEntity)}
                >
                  CANCEL
                </QuiqButton>
              )}
              {shouldRenderDetails && (
                <QuiqButton
                  color="green"
                  className="Entity__edit"
                  onClick={this.toggleEdit}
                  isLoading={isRequesting(actions.editEntity)}
                  isDisabled={isSaveBtnDisabled}
                >
                  {this.state.isEditing ? "SAVE" : "EDIT"}
                </QuiqButton>
              )}
            </div>
          </div>
          <Tabs
            className="Entity__tabs"
            value={activeTab}
            onChange={this.handleTabChange}
            classes={{ indicator: "Entity__tabs__indicator" }}
          >
            <Tab
              className="Entity__tabs__tab"
              label="DETAILS"
              classes={{
                labelContainer: "Entity__tabs__tab__label",
                selected: "Entity__tabs__tab--selected"
              }}
              disableRipple={true}
            />
            <Tab
              className="Entity__tabs__tab"
              label="SPREADS"
              classes={{
                labelContainer: "Entity__tabs__tab__label",
                selected: "Entity__tabs__tab--selected"
              }}
              disableRipple={true}
              disabled={!(this.props.entity && this.props.entity.entityId)}
            />
            <Tab
              className="Entity__tabs__tab"
              classes={{
                labelContainer: "Entity__tabs__tab__label",
                selected: "Entity__tabs__tab--selected"
              }}
              label="RULES"
              onClick={this.loadEntityRules}
              disableRipple={true}
              disabled={!(this.props.entity && this.props.entity.entityId)}
            />
            <Tab
              className="Entity__tabs__tab"
              classes={{
                labelContainer: "Entity__tabs__tab__label",
                selected: "Entity__tabs__tab--selected"
              }}
              label="HISTORY"
              onClick={this.loadEntityAudits}
              disableRipple={true}
              disabled={!(this.props.entity && this.props.entity.entityId)}
            />
          </Tabs>
        </div>
        {shouldRenderDetails && this.renderEntityDetails()}
        {shouldRenderHistory && this.renderEntityHistory()}
        {shouldRenderRules && this.renderEntityRules()}
        {shouldRenderSpreads && this.renderEntitySpreads()}
        <DeleteRuleModal
          isOpen={isDeleteRuleModalOpen}
          onClose={this.closeDeleteRuleModal}
          onConfirm={this.deleteRule}
        />
      </div>
    );
  }

  private removeRuleFromLocalState = () => {
    if (!this.state.entityRules || !this.state.ruleToDelete) {
      return;
    }

    const ruleIndex = findIndex(
      propEq("ruleId", this.state.ruleToDelete.ruleId),
      this.state.entityRules
    );
    const newRules = remove(ruleIndex, 1, this.state.entityRules);
    this.setState({ entityRules: newRules });
  };

  private onDeleteRuleComplete = () => {
    this.removeRuleFromLocalState();
    this.closeDeleteRuleModal();
  };

  private loadEntityAudits = async () => {
    this.setState({ entityAudits: null });
    const entityAudits: EntityChangeModel[] = await api.entities.getAudits(
      (this.props.entity && this.props.entity.entityId) || ""
    );

    this.setState({ entityAudits });
  };

  private loadEntityRules = async () => {
    this.setState({ entityRules: null });
    const entityRules: RuleChangeLogItem[] = await api.rules.getRules(
      (this.props.entity && this.props.entity.entityId) || ""
    );

    this.setState({ entityRules });
  };

  private getEntityName = () => {
    return this.props.entity ? this.props.entity.name : "";
  };

  private renderEntityHistory = () => {
    return <EntityAuditTrail audits={this.state.entityAudits} />;
  };

  private renderEntityRules = () => {
    if (this.state.entityRules === null) {
      return <LoadingSpinner />;
    }

    if (this.state.entityRules.length < 1) {
      return (
        <div className="Entity__rules">
          <span>There are no rules for this entity...</span>
        </div>
      );
    }

    return (
      <div className="Entity__rules__content">
        <div className="Entity__rules">
          <div className="Entity__rules__container">
            {this.state.entityRules.map((rule, index) => {
              return (
                <RuleCard
                  rule={rule}
                  key={`${rule.nodeId}-${index}`}
                  openDeleteRuleModal={this.openDeleteRuleModal}
                />
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  private renderEntityDetails = () => {
    if (this.state.isEditing) {
      return this.renderEntityInfoEdit();
    }
    return this.renderEntityInfoDisplay();
  };

  private renderEntitySpreads = () => {
    return <Documents history={this.props.history} />;
  };

  private renderEntityInfoDisplay = () => {
    if (this.props.entity === null || isRequesting(actions.editEntity)) {
      return <LoadingSpinner />;
    }
    return (
      <div className="Entity__info">
        <div className="Entity__info__item">
          <label className="Entity_info_item--label">
            {ENTITY_COLUMNS.NAME}
          </label>
          <span className="Entity__info__item__content">
            {formatEntityValue(this.props.entity.name)}
          </span>
        </div>
        <div className="Entity__info__item">
          <label className="Entity_info_item--label">
            {ENTITY_COLUMNS.CITY}
          </label>
          <span className="Entity__info__item__content">
            {formatEntityValue(this.props.entity.city)}
          </span>
        </div>
        <div className="Entity__info__item">
          <label className="Entity_info_item--label">
            {ENTITY_COLUMNS.COUNTRY}
          </label>
          <span className="Entity__info__item__content">
            {formatEntityValue(this.props.entity.country)}
          </span>
        </div>
        <div className="Entity__info__item">
          <label className="Entity_info_item--label">
            {ENTITY_COLUMNS.INDUSTRY}
          </label>
          <span className="Entity__info__item__content">
            {formatEntityValue(this.props.entity.industry)}
          </span>
        </div>
        <div className="Entity__info__item">
          <label className="Entity_info_item--label">
            {ENTITY_COLUMNS.CLIENT_ENTITY_ID}
          </label>
          <span className="Entity__info__item__content">
            {formatEntityValue(this.props.entity.clientEntityID || "")}
          </span>
        </div>
      </div>
    );
  };

  private renderEntityInfoEdit = () => {
    if (this.props.entity !== null) {
      return (
        <MuiThemeProvider theme={materialTheme}>
          <div className="Entity__info">
            <div className="Entity__edit__item">
              <div className="Entity__info__item">
                <label className="Entity_info_item--label">
                  {ENTITY_COLUMNS.NAME}
                </label>
                <InputBase
                  className="Entity__info__text__input"
                  defaultValue={formatEntityValue(this.props.entity.name)}
                  placeholder="Entity Name"
                  onChange={this.handleNameChange}
                  inputProps={{ maxLength: 200 }}
                />
              </div>
              <EditIcon />
            </div>
            <div className="Entity__edit__item">
              <div className="Entity__info__item">
                <label className="Entity_info_item--label">
                  {ENTITY_COLUMNS.CITY}
                </label>
                <InputBase
                  className="Entity__info__text__input"
                  defaultValue={formatEntityValue(this.props.entity.city)}
                  placeholder="City"
                  onChange={this.handleCityChange}
                  inputProps={{ maxLength: 200 }}
                />
              </div>
              <EditIcon />
            </div>
            <div className="Entity__edit__item">
              <div className="Entity__info__item">
                <label className="Entity_info_item--label">
                  {ENTITY_COLUMNS.COUNTRY}
                </label>
                <Select
                  className="Entity__info__select"
                  options={countryOptions}
                  defaultValue={{
                    value: this.props.entity.country,
                    label: this.props.entity.country
                  }}
                  classNamePrefix="Entity__info__select"
                  onChange={this.handleCountryChange as any}
                />
              </div>
              <EditIcon />
            </div>
            <div className="Entity__edit__item">
              <div className="Entity__info__item">
                <label className="Entity_info_item--label">
                  {ENTITY_COLUMNS.INDUSTRY}
                </label>
                <Select
                  className="Entity__info__select"
                  options={industryOptions}
                  classNamePrefix="Entity__info__select"
                  defaultValue={{
                    value: this.props.entity.industry,
                    label: this.props.entity.industry
                  }}
                  onChange={this.handleIndustryChange as any}
                />
              </div>
              <EditIcon />
            </div>
            <div className="Entity__edit__item">
              <div className="Entity__info__item">
                <label className="Entity_info_item--label">
                  {ENTITY_COLUMNS.CLIENT_ENTITY_ID}
                </label>
                <InputBase
                  className="Entity__info__text__input"
                  defaultValue={formatEntityValue(
                    this.props.entity.clientEntityID || ""
                  )}
                  placeholder="Client Entity ID"
                  onChange={this.handleClientEntityIdChange}
                  inputProps={{ maxLength: 200 }}
                  disabled={false}
                />
              </div>
              <EditIcon />
            </div>
          </div>
        </MuiThemeProvider>
      );
    }
    return <LoadingSpinner />;
  };

  private showAlertBanner = (isSuccess: boolean, message?: string) => {
    const type = isSuccess
      ? ALERT_BANNER_TYPES.SUCCESS
      : ALERT_BANNER_TYPES.ERROR;

    if (!message) {
      message = isSuccess
        ? "Success - Entity Edited"
        : "Error - Entity Edit Failed";
    }

    const options = {
      type,
      message
    };

    this.props.showAlertBanner(options);
  };

  private saveEdits = () => {
    const { entity } = this.props;
    const { newEntity } = this.state;
    if (newEntity) {
      if (
        (newEntity.name && newEntity.name.length < 1) ||
        (newEntity.industry && newEntity.industry.length < 1) ||
        (newEntity.city && newEntity.city.length < 1) ||
        (newEntity.country && newEntity.country.length < 1)
      ) {
        return;
      }

      if (
        entity &&
        newEntity.name === entity.name &&
        newEntity.industry === entity.industry &&
        newEntity.city === entity.city &&
        newEntity.country === entity.country &&
        newEntity.clientEntityID === entity.clientEntityID
      ) {
        return;
      }

      this.props.editEntity(newEntity).catch(error => {
        this.showAlertBanner(false, error.message);
      });
    }
  };
  private handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (this.state.newEntity) {
      const newEntity = {
        ...this.state.newEntity,
        name: e.target.value
      };
      this.setState({ newEntity });
    }
  };
  private handleIndustryChange = (option: DropdownOption) => {
    if (this.state.newEntity) {
      const newEntity = {
        ...this.state.newEntity,
        industry: option.value
      };
      this.setState({ newEntity });
    }
  };
  private handleCountryChange = (option: DropdownOption) => {
    if (this.state.newEntity) {
      const newEntity = {
        ...this.state.newEntity,
        country: option.value
      };
      this.setState({ newEntity });
    }
  };
  private handleCityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (this.state.newEntity) {
      const newEntity = {
        ...this.state.newEntity,
        city: e.target.value
      };
      this.setState({ newEntity });
    }
  };
  private handleClientEntityIdChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (this.state.newEntity) {
      const newEntity = {
        ...this.state.newEntity,
        clientEntityID: e.target.value
      };
      this.setState({ newEntity });
    }
  };
}

export default connect(
  selectorsToProps({
    requestProgress: getRequestProgress,
    entity: getEntityViewState
  }),
  {
    editEntity: editEntity.action,
    getEntity: getEntity.action,
    deleteRulesAction: deleteRules.action,
    resetEntity,
    showAlertBanner
  }
)(EntityView);



// WEBPACK FOOTER //
// ./src/entityView/EntityView.tsx